// =============================================================================
// features/account/personalEmail.ts — Regras PURAS do e-mail pessoal (F14-S04 / F58-S15).
//
// Fonte única da validação e dos textos de erro do e-mail pessoal. Quem usa:
//   - PersonalEmailModal (aviso bloqueante para quem passou do primeiro acesso sem ele);
//   - tela de primeiro acesso (F58-S15, decisão D14): coleta o e-mail no mesmo fluxo.
//
// Sem React, sem rede: testável em vitest puro.
//
// LGPD (doc 17 §14.2): e-mail pessoal é dado pessoal. Este módulo nunca loga o valor.
// O rascunho (`rememberPersonalEmailDraft`) vive SÓ em memória, preso ao id do usuário,
// e some quando é lido com sucesso, descartado ou quando a aba recarrega.
// =============================================================================

/** Mesmo limite do corpo da API (`setPersonalEmailBodySchema`). */
export const PERSONAL_EMAIL_MAX_LENGTH = 320;

/** RFC 5322 simplificado — a validação real é a do backend (Zod). */
export const PERSONAL_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PERSONAL_EMAIL_REQUIRED_MESSAGE = 'Informe seu email pessoal';
export const PERSONAL_EMAIL_INVALID_MESSAGE = 'Informe um email válido';
export const PERSONAL_EMAIL_CONFLICT_MESSAGE =
  'Este email já está registrado por outro agente desta organização.';
const PERSONAL_EMAIL_GENERIC_MESSAGE = 'Ocorreu um erro. Tente novamente.';

/** O que a API grava: o valor sem espaços nas pontas (o servidor também faz `.trim()`). */
export function normalizePersonalEmail(raw: string): string {
  return raw.trim();
}

/**
 * Mensagem de erro do campo, ou `null` se o valor é aceitável.
 * Vazio é erro (a obrigatoriedade é de quem chama: no primeiro acesso o campo pode ficar
 * em branco, então o formulário só chama esta função quando há algo digitado).
 */
export function validatePersonalEmail(raw: string): string | null {
  const value = normalizePersonalEmail(raw);
  if (value.length === 0) return PERSONAL_EMAIL_REQUIRED_MESSAGE;
  if (value.length > PERSONAL_EMAIL_MAX_LENGTH || !PERSONAL_EMAIL_PATTERN.test(value)) {
    return PERSONAL_EMAIL_INVALID_MESSAGE;
  }
  return null;
}

// ─── Erro da API → mensagem ───────────────────────────────────────────────────

export type PersonalEmailErrorKind = 'conflict' | 'other';

export interface PersonalEmailErrorView {
  kind: PersonalEmailErrorKind;
  message: string;
}

/** Traduz o erro de POST /api/account/personal-email. Nunca ecoa o e-mail digitado. */
export function mapPersonalEmailError(error: unknown): PersonalEmailErrorView {
  if (typeof error === 'object' && error !== null) {
    const e = error as { code?: unknown; status?: unknown; message?: unknown };
    if (e.code === 'PERSONAL_EMAIL_CONFLICT' || e.status === 409) {
      return { kind: 'conflict', message: PERSONAL_EMAIL_CONFLICT_MESSAGE };
    }
  }
  const message = error instanceof Error ? error.message : PERSONAL_EMAIL_GENERIC_MESSAGE;
  return { kind: 'other', message: message.length > 0 ? message : PERSONAL_EMAIL_GENERIC_MESSAGE };
}

// ─── Gravar depois do primeiro acesso ────────────────────────────────────────

export type PersonalEmailSaveResult =
  /** Nada foi digitado: nada a gravar. */
  | { status: 'skipped' }
  | { status: 'saved' }
  /** A API recusou ou a rede falhou. O primeiro acesso JÁ está feito e não é desfeito. */
  | { status: 'failed'; error: PersonalEmailErrorView; email: string };

/**
 * Grava o e-mail pessoal logo depois de o primeiro acesso ter dado certo.
 * NUNCA lança: o que acontecer aqui não pode desfazer nem travar a troca de senha.
 * Falhou → devolve `failed` e o guard do modal (`usePersonalEmailGuard`) cobra depois.
 */
export async function savePersonalEmailBestEffort(
  save: (body: { personalEmail: string }) => Promise<unknown>,
  raw: string | null | undefined,
): Promise<PersonalEmailSaveResult> {
  const email = normalizePersonalEmail(raw ?? '');
  if (email.length === 0) return { status: 'skipped' };
  if (validatePersonalEmail(email) !== null) {
    // O formulário já barra isso; defesa para não mandar lixo à API.
    return {
      status: 'failed',
      error: { kind: 'other', message: PERSONAL_EMAIL_INVALID_MESSAGE },
      email,
    };
  }
  try {
    await save({ personalEmail: email });
    return { status: 'saved' };
  } catch (error: unknown) {
    return { status: 'failed', error: mapPersonalEmailError(error), email };
  }
}

// ─── Rascunho em memória (para o modal não pedir de novo o que já foi digitado) ──

let draft: { userId: string; email: string } | null = null;

/** Guarda o e-mail que não conseguiu ser gravado. Só memória; preso ao usuário. */
export function rememberPersonalEmailDraft(userId: string, email: string): void {
  draft = { userId, email };
}

/** Devolve o rascunho só para o MESMO usuário (troca de conta na mesma aba não vaza). */
export function peekPersonalEmailDraft(userId: string | null | undefined): string {
  if (draft === null || userId === null || userId === undefined) return '';
  return draft.userId === userId ? draft.email : '';
}

export function clearPersonalEmailDraft(): void {
  draft = null;
}
